import Immutable from 'immutable';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { AbilityType, toAbilityName } from '../../api/DnD/abilities';
import { DiceType } from '../../api/DnD/dices';
import {
  AppState,
  TutorialCheckOutcome,
  TutorialCombatOutcome,
  TutorialState,
  TutorialStep,
} from '../state';

type TutorialStepInfo = {
  eyebrow: string;
  title: string;
  description: string;
  objective: string;
  reward?: string;
  attempts?: string;
  status?: string;
  tip?: string;
};

type TutorialEncounterViewModel = {
  variant: 'obstacle' | 'enemy';
  eyebrow: string;
  title: string;
  badge: string;
  status: string;
  avatarFileName?: string;
  hp?: {
    current: number;
    max: number;
    percent: number;
  };
};

const TutorialFlow: TutorialStep[] = [
  'tap_to_roll',
  'select_strength_modifier',
  'clear_log_with_strength',
  'select_dexterity_modifier',
  'cross_rope_with_dexterity',
  'defeat_tutorial_enemy',
  'completed',
];

const TUTORIAL_ENEMY_NAME = 'Orc Marauder';
const TUTORIAL_ENEMY_AVATAR = 'male half orc.png';
const TUTORIAL_ENEMY_MAX_HP = 3;
const LOG_TARGET = 5;
const ROPE_TARGET = 5;
const COMBAT_HIT_TARGET = 15;
const tutorialAttackSelection = Immutable.Map<DiceType, number>().set(DiceType.d20, 1);
const tutorialDamageSelection = Immutable.Map<DiceType, number>().set(DiceType.d6, 1);

const StepRank = TutorialFlow.reduce(
  (acc, step, index) => {
    acc[step] = index;
    return acc;
  },
  {} as Record<TutorialStep, number>,
);

export const selectTutorialState = (state: AppState): TutorialState => state.tutorial;
export const selectTutorialEnabled = (state: AppState): boolean => state.tutorial.enabled;
export const selectTutorialCompleted = (state: AppState): boolean => state.tutorial.completed;
export const selectTutorialStep = (state: AppState): TutorialStep => state.tutorial.step;
export const selectTutorialCheckOutcome = (state: AppState): TutorialCheckOutcome => state.tutorial.checkOutcome;
export const selectTutorialLastCheckTotal = (state: AppState): number | undefined => state.tutorial.lastCheckTotal;
export const selectTutorialCombatPhase = (state: AppState) => state.tutorial.combatPhase;
export const selectTutorialCombatOutcome = (state: AppState): TutorialCombatOutcome => state.tutorial.combatOutcome;
export const selectTutorialEnemyHp = (state: AppState): number => state.tutorial.enemyHp;
export const selectTutorialLastCombatRoll = (state: AppState): number | undefined => state.tutorial.lastCombatRoll;
export const selectTutorialRollModifier = (state: AppState): AbilityType | undefined => state.dices.rollModifier;

export const selectTutorialStepInfo = createSelector(
  [
    selectTutorialStep,
    selectTutorialCheckOutcome,
    selectTutorialLastCheckTotal,
    selectTutorialCombatPhase,
    selectTutorialCombatOutcome,
    selectTutorialEnemyHp,
    selectTutorialLastCombatRoll,
  ],
  (step, checkOutcome, lastCheckTotal, combatPhase, combatOutcome, enemyHp, lastCombatRoll): TutorialStepInfo => {
    switch (step) {
      case 'tap_to_roll':
        return {
          eyebrow: 'Prologue',
          title: 'First Roll',
          description: 'The road opens in front of you. Start with a simple throw.',
          objective: 'Drop your first d20 anywhere on the board.',
          reward: 'Reward: The journey begins.',
          tip: 'Tip: For this opening throw, any single d20 roll is enough.',
        };
      case 'select_strength_modifier':
        return {
          eyebrow: 'Obstacle',
          title: 'Lift The Log',
          description: 'A fallen log blocks the road. This is a Strength check.',
          objective: 'Tap Strength. It is the only stat visible for now.',
          reward: 'Reward: The log can be tested with a roll.',
          tip: 'Tip: Selecting a stat turns the next d20 into that ability check.',
        };
      case 'clear_log_with_strength':
        return {
          eyebrow: 'Strength Check',
          title: 'Move The Log',
          description: 'Roll with Strength and reach 5 or more to drag the log aside.',
          objective: `Throw the d20 with Strength selected and beat ${LOG_TARGET}.`,
          status: getCheckStatus('log', checkOutcome, lastCheckTotal),
          reward: 'Reward: Dexterity enters the story next.',
          tip: 'Tip: If you fail, nothing fatal happens. You just brace and try again.',
        };
      case 'select_dexterity_modifier':
        return {
          eyebrow: 'Obstacle',
          title: 'Take The Rope',
          description: 'The path narrows into a rope traverse. This one calls for Dexterity.',
          objective: 'Tap Dexterity. Strength stays visible, but this test wants finesse.',
          reward: 'Reward: The rope can be crossed with a roll.',
          tip: 'Tip: New stats appear as the story asks for them.',
        };
      case 'cross_rope_with_dexterity':
        return {
          eyebrow: 'Dexterity Check',
          title: 'Cross The Rope',
          description: 'Keep your balance and reach 5 or more to make it across.',
          objective: `Throw the d20 with Dexterity selected and beat ${ROPE_TARGET}.`,
          status: getCheckStatus('rope', checkOutcome, lastCheckTotal),
          reward: 'Reward: An enemy waits on the far side.',
          tip: 'Tip: A bad roll only means you steady yourself and try again.',
        };
      case 'defeat_tutorial_enemy':
        return {
          eyebrow: 'Encounter',
          title: 'Drop The Marauder',
          description: 'Hit chance uses Dexterity on the d20. Damage uses Strength on the d6.',
          objective:
            combatOutcome === 'victory'
              ? 'The marauder is down. The tutorial ends on your win.'
              : combatPhase === 'damage'
                ? 'Hit confirmed. Roll your d6 with Strength for damage.'
                : 'Roll the d20 with Dexterity and beat 15 to land a hit.',
          status: getCombatStatus(combatOutcome, combatPhase, enemyHp, lastCombatRoll),
          reward: 'Reward: This chapter of the tutorial is complete.',
          tip: 'Tip: The fight auto-switches between Dexterity to hit and Strength for damage.',
        };
      case 'completed':
        return {
          eyebrow: 'Complete',
          title: 'Tutorial Chapter Complete',
          description: 'You rolled free, cleared a Strength obstacle, crossed a Dexterity obstacle, and won a fight.',
          objective: 'More story beats can now be layered on top of this structure.',
          tip: 'Tip: Next chapters can introduce the remaining stats the same way.',
        };
    }
  },
);

export const selectTutorialProgress = createSelector([selectTutorialStep], (step) => {
  const total = TutorialFlow.length - 1;
  const stepIndex = StepRank[step];
  const current = step === 'completed' ? total : Math.min(stepIndex + 1, total);

  return { current, total };
});

export const selectTutorialShowsAbilityStrip = createSelector(
  [selectTutorialEnabled, selectTutorialStep],
  (enabled, step): boolean =>
    !enabled ||
    step === 'select_strength_modifier' ||
    step === 'clear_log_with_strength' ||
    step === 'select_dexterity_modifier' ||
    step === 'cross_rope_with_dexterity' ||
    step === 'defeat_tutorial_enemy',
);

export const selectTutorialAllowsAbilitySelection = createSelector(
  [selectTutorialEnabled, selectTutorialStep],
  (enabled, step): boolean => !enabled || step === 'select_strength_modifier' || step === 'select_dexterity_modifier',
);

export const selectTutorialVisibleAbilityTypes = createSelector(
  [selectTutorialEnabled, selectTutorialStep],
  (enabled, step): AbilityType[] | undefined => {
    if (!enabled) {
      return undefined;
    }

    if (step === 'select_strength_modifier' || step === 'clear_log_with_strength') {
      return [AbilityType.STR];
    }

    if (step === 'select_dexterity_modifier' || step === 'cross_rope_with_dexterity') {
      return [AbilityType.STR, AbilityType.DEX];
    }

    if (step === 'defeat_tutorial_enemy') {
      return [AbilityType.STR, AbilityType.DEX];
    }

    return [];
  },
);

export const selectTutorialShowsDiceButton = createSelector(
  [selectTutorialEnabled],
  (enabled): boolean => !enabled,
);

export const selectTutorialAllowsDicePanel = createSelector(
  [selectTutorialEnabled],
  (enabled): boolean => !enabled,
);

export const selectTutorialShowsCharacterButtons = createSelector(
  [selectTutorialEnabled],
  (enabled): boolean => !enabled,
);

export const selectTutorialShowsMenuButton = createSelector(
  [selectTutorialEnabled],
  (enabled): boolean => !enabled,
);

export const selectTutorialShowsInventory = createSelector(
  [selectTutorialEnabled],
  (enabled): boolean => !enabled,
);

export const selectTutorialShowsDicePanelExtras = createSelector(
  [selectTutorialEnabled],
  (enabled): boolean => !enabled,
);

export const selectTutorialHidesIdleDice = createSelector(
  [selectTutorialEnabled, selectTutorialStep],
  (enabled, step): boolean => enabled && step === 'tap_to_roll',
);

export const selectTutorialLockedDiceSelection = createSelector(
  [selectTutorialEnabled, selectTutorialStep, selectTutorialCombatPhase],
  (enabled, step, combatPhase): Immutable.Map<DiceType, number> | undefined => {
    if (!enabled || step !== 'defeat_tutorial_enemy') {
      return undefined;
    }

    return combatPhase === 'damage' ? tutorialDamageSelection : tutorialAttackSelection;
  },
);

export const selectTutorialEncounter = createSelector(
  [
    selectTutorialEnabled,
    selectTutorialStep,
    selectTutorialCheckOutcome,
    selectTutorialLastCheckTotal,
    selectTutorialCombatPhase,
    selectTutorialCombatOutcome,
    selectTutorialEnemyHp,
    selectTutorialLastCombatRoll,
  ],
  (
    enabled,
    step,
    checkOutcome,
    lastCheckTotal,
    combatPhase,
    combatOutcome,
    enemyHp,
    lastCombatRoll,
  ): TutorialEncounterViewModel | undefined => {
    if (!enabled) {
      return undefined;
    }

    if (step === 'select_strength_modifier' || step === 'clear_log_with_strength') {
      return {
        variant: 'obstacle',
        eyebrow: 'Blocked Path',
        title: 'Fallen Log',
        badge: 'STR 5+',
        status: getCheckStatus('log', checkOutcome, lastCheckTotal),
      };
    }

    if (step === 'select_dexterity_modifier' || step === 'cross_rope_with_dexterity') {
      return {
        variant: 'obstacle',
        eyebrow: 'Narrow Traverse',
        title: 'Rope Crossing',
        badge: 'DEX 5+',
        status: getCheckStatus('rope', checkOutcome, lastCheckTotal),
      };
    }

    if (step === 'defeat_tutorial_enemy') {
      return {
        variant: 'enemy',
        eyebrow: 'Target',
        title: TUTORIAL_ENEMY_NAME,
        badge: combatOutcome === 'victory' ? 'Defeated' : combatPhase === 'damage' ? 'Roll D6' : 'Roll D20',
        status: getCombatStatus(combatOutcome, combatPhase, enemyHp, lastCombatRoll),
        avatarFileName: TUTORIAL_ENEMY_AVATAR,
        hp: {
          current: enemyHp,
          max: TUTORIAL_ENEMY_MAX_HP,
          percent: Math.max(0, (enemyHp / TUTORIAL_ENEMY_MAX_HP) * 100),
        },
      };
    }

    return undefined;
  },
);

const getCheckStatus = (
  obstacle: 'log' | 'rope',
  checkOutcome: TutorialCheckOutcome,
  lastCheckTotal?: number,
): string => {
  const abilityName = obstacle === 'log' ? toAbilityName(AbilityType.STR) : toAbilityName(AbilityType.DEX);
  const target = obstacle === 'log' ? LOG_TARGET : ROPE_TARGET;
  const action =
    obstacle === 'log'
      ? 'The log blocks the road.'
      : 'The rope sways, but a miss only means you try again.';

  switch (checkOutcome) {
    case 'success':
      return `${abilityName} check ${lastCheckTotal ?? 0}. You clear the obstacle.`;
    case 'failure':
      return `${abilityName} check ${lastCheckTotal ?? 0}. You need ${target} or higher.`;
    case 'idle':
    default:
      return `${action} Reach ${target}+ on your ${abilityName} check.`;
  }
};

const getCombatStatus = (
  combatOutcome: TutorialCombatOutcome,
  combatPhase: AppState['tutorial']['combatPhase'],
  enemyHp: number,
  lastCombatRoll?: number,
): string => {
  switch (combatOutcome) {
    case 'miss':
      return `Dexterity attack total ${lastCombatRoll ?? 0}. You need 16 or higher to hit.`;
    case 'hit':
      return `Dexterity attack total ${lastCombatRoll ?? 0}. Clean hit. Roll your Strength damage now.`;
    case 'damage':
      return `Strength damage ${lastCombatRoll ?? 0}. Enemy HP now ${enemyHp}/${TUTORIAL_ENEMY_MAX_HP}.`;
    case 'victory':
      return 'Enemy defeated. The path is yours.';
    case 'idle':
    default:
      return combatPhase === 'damage'
        ? 'The strike connects. Roll your d6 with Strength for damage.'
        : `Open with a d20 + Dexterity attack roll and beat ${COMBAT_HIT_TARGET}.`;
  }
};

export const useTutorialEnabled = (): boolean => useSelector(selectTutorialEnabled);
export const useTutorialCompleted = (): boolean => useSelector(selectTutorialCompleted);
export const useTutorialStep = (): TutorialStep => useSelector(selectTutorialStep);
export const useTutorialStepInfo = (): TutorialStepInfo => useSelector(selectTutorialStepInfo);
export const useTutorialProgress = (): { current: number; total: number } => useSelector(selectTutorialProgress);
export const useTutorialShowsAbilityStrip = (): boolean => useSelector(selectTutorialShowsAbilityStrip);
export const useTutorialAllowsAbilitySelection = (): boolean => useSelector(selectTutorialAllowsAbilitySelection);
export const useTutorialVisibleAbilityTypes = (): AbilityType[] | undefined =>
  useSelector(selectTutorialVisibleAbilityTypes);
export const useTutorialShowsDiceButton = (): boolean => useSelector(selectTutorialShowsDiceButton);
export const useTutorialAllowsDicePanel = (): boolean => useSelector(selectTutorialAllowsDicePanel);
export const useTutorialShowsCharacterButtons = (): boolean => useSelector(selectTutorialShowsCharacterButtons);
export const useTutorialShowsMenuButton = (): boolean => useSelector(selectTutorialShowsMenuButton);
export const useTutorialShowsInventory = (): boolean => useSelector(selectTutorialShowsInventory);
export const useTutorialShowsDicePanelExtras = (): boolean => useSelector(selectTutorialShowsDicePanelExtras);
export const useTutorialHidesIdleDice = (): boolean => useSelector(selectTutorialHidesIdleDice);
export const useTutorialLockedDiceSelection = (): Immutable.Map<DiceType, number> | undefined =>
  useSelector(selectTutorialLockedDiceSelection);
export const useTutorialEncounter = (): TutorialEncounterViewModel | undefined => useSelector(selectTutorialEncounter);
